import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import {
  getPortfolio,
  updatePortfolio,
  addSource,
  updateSource,
  deleteSource,
  queueRun,
  getLatestRun,
} from "../../api/loadPortfolios";
import {
  PageLayout,
  PageHeader,
  Section,
  Card,
  CardHeader,
  Button,
  Badge,
  PageSpinner,
  MetricCard,
} from "../../components";
import { LoadSourceCard, LoadSourceGroupCard } from "../../features/load-portfolio/components/LoadSourceCard";
import { LoadAddSourceModal } from "../../features/load-portfolio/components/LoadAddSourceModal";
import { LoadEditSourceModal } from "../../features/load-portfolio/components/LoadEditSourceModal";
import { LoadCapacityBar } from "../../features/load-portfolio/components/LoadCapacityBar";
import { LoadDispatchChart } from "../../features/load-portfolio/components/LoadDispatchChart";
import { LoadGenerationChart } from "../../features/load-portfolio/components/LoadGenerationChart";
import { LoadImpactSummaryCard } from "../../features/load-portfolio/components/LoadImpactSummaryCard";
import { LoadGridSupplyPanel } from "../../features/load-portfolio/components/LoadGridSupplyPanel";
import { LoadImpactExplorer } from "../../features/load-portfolio/components/LoadImpactExplorer";
import { LoadEmissionsCard } from "../../features/load-portfolio/components/LoadEmissionsCard";
import { LoadSCRComparisonCard } from "../../features/load-portfolio/components/LoadSCRComparisonCard";
import { LoadProfileUpload } from "../../features/load-portfolio/components/LoadProfileUpload";
import type {
  LoadPortfolioDetail as LoadPortfolioDetailType,
  LoadSourceCreateInput,
  LoadSourceEditInput,
  LoadSource,
  LoadRun,
  SourceType,
  SourceGroup,
} from "../../features/load-portfolio/domain/types";

/** Group sources with same type, capacity, and config into stacked groups */
function groupSources(sources: LoadSource[]): SourceGroup[] {
  const map = new Map<string, SourceGroup>();
  for (const src of sources) {
    // Strip " #N" suffix to get base name
    const baseName = src.source_name.replace(/\s+#\d+$/, "");
    // Build config fingerprint
    const type = src.source_type;
    let configFp = "";
    if (type === "ng") configFp = src.ng_turbine_type || "";
    else if (type === "solar" || type === "wind") configFp = src.tech_type_id || "";
    else if (type === "bess")
      configFp = `${src.bess_duration_hours || 4}_${src.bess_round_trip_eff || 0.85}`;
    const key = `${type}|${src.nameplate_capacity_mw}|${configFp}|${baseName}`;
    const existing = map.get(key);
    if (existing) {
      existing.sources.push(src);
    } else {
      map.set(key, {
        key,
        baseName,
        sourceType: type as SourceType,
        capacityEach: src.nameplate_capacity_mw,
        sources: [src],
      });
    }
  }
  return Array.from(map.values());
}

export default function LoadPortfolioDetail() {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [showAddSource, setShowAddSource] = useState(false);
  const [editingSource, setEditingSource] = useState<LoadSource | null>(null);
  const [sourcesChanged, setSourcesChanged] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [editingLoad, setEditingLoad] = useState(false);
  const [loadMw, setLoadMw] = useState<number>(0);

  const {
    data: portfolio,
    isLoading,
  } = useQuery<LoadPortfolioDetailType>({
    queryKey: ["dc-portfolio", portfolioId],
    queryFn: () => getPortfolio(portfolioId!, token!),
    enabled: !!token && !!portfolioId,
  });

  // Poll for latest run if queued/running
  const { data: latestRun } = useQuery<LoadRun>({
    queryKey: ["dc-portfolio-run", portfolioId],
    queryFn: () => getLatestRun(portfolioId!, token!),
    enabled: !!token && !!portfolioId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" && sourcesChanged) {
        // Run finished — clear stale banner
        setSourcesChanged(false);
      }
      return status === "queued" || status === "running" ? 3000 : false;
    },
  });

  const run = latestRun || portfolio?.latest_run;

  const handleAddSource = async (data: LoadSourceCreateInput) => {
    await addSource(portfolioId!, data, token!);
    setSourcesChanged(true);
    queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
  };

  const handleEditSource = async (sourceId: string, data: LoadSourceEditInput) => {
    await updateSource(portfolioId!, sourceId, data, token!);
    setSourcesChanged(true);
    queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
  };

  const handleDeleteSource = async (sourceId: string) => {
    await deleteSource(portfolioId!, sourceId, token!);
    setSourcesChanged(true);
    queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
  };

  const handleUpdateLoad = async () => {
    if (!portfolioId || !token || loadMw <= 0) return;
    try {
      await updatePortfolio(portfolioId, { flat_load_mw: loadMw }, token);
      setSourcesChanged(true);
      queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
      setEditingLoad(false);
    } catch (err: any) {
      alert(err.message || "Failed to update load");
    }
  };

  const handleUploadProfile = async (profile: number[]) => {
    if (!portfolioId || !token) return;
    try {
      await updatePortfolio(portfolioId, { load_profile_json: profile } as any, token);
      setSourcesChanged(true);
      queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
    } catch (err: any) {
      alert(err.message || "Failed to upload profile");
    }
  };

  const handleClearProfile = async () => {
    if (!portfolioId || !token) return;
    try {
      await updatePortfolio(portfolioId, { load_profile_json: null } as any, token);
      setSourcesChanged(true);
      queryClient.invalidateQueries({ queryKey: ["dc-portfolio", portfolioId] });
    } catch (err: any) {
      alert(err.message || "Failed to clear profile");
    }
  };

  const handleQueueRun = async () => {
    if (!portfolioId || !token) return;
    setQueueing(true);
    try {
      await queueRun(portfolioId, portfolio?.current_phase || "btm_only", token);
      setSourcesChanged(false);
      queryClient.invalidateQueries({
        queryKey: ["dc-portfolio-run", portfolioId],
      });
    } catch (err: any) {
      alert(err.message || "Failed to queue run");
    } finally {
      setQueueing(false);
    }
  };

  if (isLoading || !portfolio) return <PageSpinner message="Loading portfolio..." />;

  // Consider a run "stuck" if queued/running for more than 10 minutes
  const runAge = run?.created_at ? Date.now() - new Date(run.created_at).getTime() : 0;
  const isStuck = (run?.status === "queued" || run?.status === "running") && runAge > 10 * 60 * 1000;
  const isRunning = !isStuck && (run?.status === "queued" || run?.status === "running");
  const hasResults = run?.status === "completed" && run.summary_json;
  const dailyDispatch = run?.dispatch_summary_json?.daily_dispatch;

  // Compute effective load MW from flat_load_mw or custom profile average
  const effectiveLoadMw = portfolio.flat_load_mw
    ?? (portfolio.load_profile_json && portfolio.load_profile_json.length > 0
      ? portfolio.load_profile_json.reduce((a, b) => a + b, 0) / portfolio.load_profile_json.length
      : null);
  const totalLoadMw = effectiveLoadMw ? effectiveLoadMw * portfolio.pue : null;

  return (
    <PageLayout>
      <PageHeader
        title={portfolio.portfolio_name}
        subtitle={portfolio.description || `${portfolio.state || ""} ${portfolio.eia_ba_code || ""}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleQueueRun}
              disabled={
                queueing ||
                isRunning ||
                (portfolio.current_phase === "btm_only" &&
                  portfolio.sources.length === 0)
              }
              title={
                portfolio.current_phase === "btm_only" &&
                portfolio.sources.length === 0
                  ? "btm_only portfolios need at least one on-site source to dispatch against. Add a source — or switch the portfolio to 'interconnected' to model the load as fully grid-served."
                  : undefined
              }
            >
              {isRunning
                ? "Running..."
                : queueing
                ? "Queueing..."
                : "Run Assessment"}
            </Button>
          </div>
        }
      />

      {/* Stale-run banner */}
      {sourcesChanged && hasResults && (
        <Section>
          <div className="flex items-center justify-between bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3">
            <span className="text-body-sm text-yellow-300">
              Sources have changed since the last run. Results may be outdated.
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleQueueRun}
              disabled={queueing || isRunning}
            >
              Re-run Assessment
            </Button>
          </div>
        </Section>
      )}

      {/* Overview Metrics */}
      <Section>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {editingLoad ? (
            <Card>
              <div className="p-3">
                <label className="text-body-sm text-text-muted block mb-1">IT Load (MW)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="w-24 bg-surface-600 border border-border rounded px-2 py-1 text-text-primary"
                    value={loadMw}
                    onChange={(e) => setLoadMw(parseFloat(e.target.value) || 0)}
                    min="1"
                    step="0.1"
                    autoFocus
                  />
                  <span className="text-text-muted text-body-sm">MW</span>
                  <button
                    className="text-quantum-400 text-body-sm hover:underline"
                    onClick={handleUpdateLoad}
                  >
                    Save
                  </button>
                  <button
                    className="text-text-muted text-body-sm hover:underline"
                    onClick={() => setEditingLoad(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Card>
          ) : (
            <div
              className="cursor-pointer"
              onClick={() => {
                setLoadMw(portfolio.flat_load_mw || 500);
                setEditingLoad(true);
              }}
            >
              <MetricCard
                label="IT Load"
                value={
                  portfolio.load_profile_json
                    ? `${Math.round(effectiveLoadMw!)} MW (profile)`
                    : portfolio.flat_load_mw
                    ? `${portfolio.flat_load_mw} MW`
                    : "Not set — click to edit"
                }
              />
            </div>
          )}
          <MetricCard label="PUE" value={portfolio.pue.toFixed(2)} />
          <MetricCard
            label="Total Load"
            value={
              totalLoadMw
                ? `${Math.round(totalLoadMw).toLocaleString()} MW`
                : "--"
            }
          />
          <MetricCard
            label="Phase"
            value={portfolio.current_phase === "btm_only" ? "BTM Only" : "Interconnected"}
          />
        </div>
      </Section>

      {/* Run Status */}
      {isRunning && (
        <Section>
          <Card>
            <div className="p-6 text-center">
              <div className="inline-flex items-center gap-2 text-quantum-400">
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                <span>
                  Assessment {run?.status}... Generating SAM profiles and computing impacts.
                </span>
              </div>
            </div>
          </Card>
        </Section>
      )}

      {/* Stuck Run */}
      {isStuck && (
        <Section>
          <div className="flex items-center justify-between bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3">
            <span className="text-body-sm text-yellow-300">
              Previous run appears stuck ({run?.status} for {Math.round(runAge / 60000)} min). You can re-queue.
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleQueueRun}
              disabled={queueing}
            >
              Re-run Assessment
            </Button>
          </div>
        </Section>
      )}

      {/* Run Error */}
      {run?.status === "failed" && (
        <Section>
          <Card>
            <div className="p-4 text-red-400">
              <strong>Run failed:</strong> {run.error_message || "Unknown error"}
            </div>
          </Card>
        </Section>
      )}

      {/* Results */}
      {hasResults && (
        <>
          {/* LEAD: grid-supplied footprint + which generators served the load */}
          {run.summary_json?.grid_supply &&
            (run.summary_json.grid_supply.meta?.grid_import_mwh ?? 0) > 0 && (
              <Section>
                <LoadGridSupplyPanel
                  portfolioName={portfolio.portfolio_name}
                  summary={run.summary_json}
                />
              </Section>
            )}

          {/* Net composition — on-site + grid, less RE */}
          {run.summary_json && (
            <Section>
              <LoadImpactSummaryCard
                portfolioName={portfolio.portfolio_name}
                summary={run.summary_json}
                dispatch={run.dispatch_summary_json}
                baCode={portfolio.eia_ba_code}
              />
            </Section>
          )}

          {/* Annual Generation Chart */}
          {dailyDispatch && dailyDispatch.length > 0 && (
            <Section>
              <LoadGenerationChart data={dailyDispatch} />
            </Section>
          )}

          {/* Dispatch Summary */}
          {run.dispatch_summary_json && (
            <Section>
              <LoadDispatchChart dispatch={run.dispatch_summary_json} />
            </Section>
          )}

          {/* Impact Explorer — full ReCiPe/USEtox breakdown (grid OR on-site) */}
          {(run.summary_json?.grid_supply?.impacts ||
            run.summary_json?.ng_impacts?.impacts) && (
            <Section>
              <LoadImpactExplorer summary={run.summary_json} />
            </Section>
          )}

          {/* SCR Comparison — with vs without emissions controls */}
          {run.summary_json?.scr_comparison && (
            <Section>
              <LoadSCRComparisonCard comparison={run.summary_json.scr_comparison} />
            </Section>
          )}

          {/* Air, Soil, Water & Resource Emissions */}
          {run.summary_json?.ng_impacts?.emissions && (
            <Section>
              <LoadEmissionsCard summary={run.summary_json} />
            </Section>
          )}
        </>
      )}

      {/* Capacity vs Load */}
      {portfolio.sources.length > 0 && (
        <Section>
          <LoadCapacityBar
            sources={portfolio.sources}
            loadMw={totalLoadMw || 0}
          />
        </Section>
      )}

      {/* Load Profile Upload */}
      <Section>
        <LoadProfileUpload
          currentFlatLoad={portfolio.flat_load_mw}
          currentProfile={portfolio.load_profile_json}
          onUpload={handleUploadProfile}
          onClearProfile={handleClearProfile}
        />
      </Section>

      {/* Generation Sources */}
      <Section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-h4 text-text-primary">Generation Sources</h3>
          <Button variant="secondary" onClick={() => setShowAddSource(true)}>
            Add Source
          </Button>
        </div>

        {portfolio.sources.length > 0 ? (
          <div className="space-y-2">
            {groupSources(portfolio.sources).map((group) =>
              group.sources.length === 1 ? (
                <LoadSourceCard
                  key={group.sources[0].source_id}
                  source={group.sources[0]}
                  onDelete={handleDeleteSource}
                  onEdit={(s) => setEditingSource(s)}
                />
              ) : (
                <LoadSourceGroupCard
                  key={group.key}
                  group={group}
                  onDelete={handleDeleteSource}
                  onEdit={(s) => setEditingSource(s)}
                />
              )
            )}
          </div>
        ) : (
          <Card>
            <div className="p-6 text-center text-text-muted">
              {portfolio.current_phase === "interconnected" ? (
                <>
                  This portfolio is{" "}
                  <span className="text-text-primary font-medium">
                    grid-tied
                  </span>{" "}
                  — all load is served by the grid's marginal mix.
                  <br />
                  Add on-site sources only if you want to offset some of the
                  residual; otherwise you can run the assessment as-is.
                </>
              ) : (
                <>
                  No sources configured. Add solar, wind, NG, or BESS sources
                  to build your generation portfolio.
                </>
              )}
            </div>
          </Card>
        )}
      </Section>

      <LoadAddSourceModal
        isOpen={showAddSource}
        onClose={() => setShowAddSource(false)}
        onSubmit={handleAddSource}
      />

      <LoadEditSourceModal
        isOpen={!!editingSource}
        source={editingSource}
        onClose={() => setEditingSource(null)}
        onSubmit={handleEditSource}
      />
    </PageLayout>
  );
}
